// OrderDetailPrimitives.jsx — generic, service-agnostic building blocks shared
// by the three independent order-detail dialogs (PickupDeliveryOrderDetailDialog,
// WashFoldOrderDetailDialog, SneakerCleaningOrderDetailDialog). These are plain
// presentational pieces and pure utility functions (currency/addon helpers,
// receipt cards, evidence thumbnails, the recurrence editor) — not business
// logic that differs per service — so, like the public-form verification-code
// mechanism stayed shared on the backend side of this same separation, they're
// meant to stay shared here too.
import { useState, useEffect, useCallback, forwardRef } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import {
  RefreshCw, X, ImageIcon, CheckCircle2, AlertTriangle, Eye,
  ShieldCheck, ShieldX, Clock, ChevronDown, ChevronUp, Calendar,
  Edit2, Camera, ZoomIn, CalendarDays, CalendarRange, Repeat,
} from "lucide-react";
import { toast } from "sonner";
import { normalizeServiceType, buildDisplayBreakdown, calcDeliveryFee } from "../utils";
import { useLocale } from "../../../context/LocaleContext";

export const parseLocalDate = (ds) => {
  if (!ds) return null;
  return ds.includes("T") ? new Date(ds) : new Date(ds + "T12:00:00");
};

export const API_URL = process.env.REACT_APP_BACKEND_URL || "";
export const getToken = () => localStorage.getItem("token") || sessionStorage.getItem("token");
export const authHdrs = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${getToken()}`,
});

export const handle401 = (res) => {
  if (res.status === 401) {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    window.location.href = "/login";
    return true;
  }
  return false;
};

export const handle403 = (res) => {
  if (res.status === 403) {
    toast.error("No tienes permiso para realizar esta acción");
    return true;
  }
  return false;
};

export const EVIDENCE_IMAGE_CONFIG = {
  pickup: { label: { en: "Pickup", es: "Recogida" } },
  weight: { label: { en: "Weight", es: "Peso" } },
  delivery: { label: { en: "Delivery", es: "Entrega" } },
};

export const buildEvidenceImageUrl = (orderId, type) =>
  `${API_URL}/api/driver/orders/${orderId}/${type}-image/view`;

export const getEvidenceImageTypes = (order) => {
  const serviceType = normalizeServiceType(order?.service_type);
  if (serviceType === "pickup_delivery" || serviceType === "wash_fold") {
    return ["pickup", "weight", "delivery"];
  }

  return ["pickup", "weight", "delivery"].filter((type) => {
    if (type === "pickup") return Boolean(order?.pickup_image_id || order?.pickup_image_url || order?.pickup_image_data);
    if (type === "weight") return Boolean(order?.weight_image_id || order?.weight_image_url || order?.weight_image_data);
    return Boolean(order?.delivery_image_id || order?.delivery_image_url || order?.delivery_image_data);
  });
};

export const normalizePayMethod = (method) => {
  const m = (method || "").toLowerCase();
  if (m === "card") return "card";
  if (m === "cash") return "cash";
  if (m === "zelle") return "zelle";
  if (m === "venmo" || m === "cashapp") return "other";
  return "other";
};

export const slugifyAddonKey = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

export const parseAddonPrice = (value, fallback = 0) => {
  const parsed = parseFloat(String(value ?? "").replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : fallback;
};

export const getAddonDisplayName = (addon, locale) =>
  locale === "es" && addon?.name_es ? addon.name_es : addon?.name;

export const CAT_LABELS = {
  home_essentials: { en: "Home Essentials", es: "Artículos del hogar" },
  bedding:         { en: "Bedding",         es: "Ropa de cama" },
  comforters:      { en: "Comforters",      es: "Edredones" },
};

export const buildAddonCategoryLabel = (categoryKey, labels, items, locale) => {
  const fromState = labels?.[categoryKey];
  if (fromState) {
    return locale === "es" ? (fromState.es || fromState.en || categoryKey) : (fromState.en || fromState.es || categoryKey);
  }
  const first = Array.isArray(items) ? items[0] : null;
  if (first?.category_label || first?.category_label_es) {
    return locale === "es"
      ? (first.category_label_es || first.category_label || categoryKey)
      : (first.category_label || first.category_label_es || categoryKey);
  }
  return categoryKey;
};

export const transformServicesConfigToAddonCatalog = (config) => {
  const categories = Array.isArray(config?.per_piece_categories) ? config.per_piece_categories : [];
  const categoryLabels = { ...CAT_LABELS };
  const catalog = categories.flatMap((cat, categoryIndex) => {
    const categoryName = cat?.category || cat?.category_es || `category_${categoryIndex + 1}`;
    const categoryKey = slugifyAddonKey(categoryName);
    categoryLabels[categoryKey] = {
      en: cat?.category || cat?.category_es || categoryName,
      es: cat?.category_es || cat?.category || categoryName,
    };

    return (Array.isArray(cat?.items) ? cat.items : []).map((item, itemIndex) => {
      const name = item?.name || item?.name_es || `item_${itemIndex + 1}`;
      return {
        id: item?.id || slugifyAddonKey(`${categoryName}_${name}`),
        name,
        name_es: item?.name_es || item?.name || name,
        price: parseAddonPrice(item?.price, 0),
        category: categoryKey,
        category_label: cat?.category || categoryName,
        category_label_es: cat?.category_es || cat?.category || categoryName,
      };
    });
  });

  return { catalog, categoryLabels };
};

export const findCatalogAddonMatch = (catalog, addon) => {
  const addonId = slugifyAddonKey(addon?.id);
  const addonName = slugifyAddonKey(addon?.name);
  const addonNameEs = slugifyAddonKey(addon?.name_es);

  return catalog.find((item) => {
    const itemId = slugifyAddonKey(item?.id);
    const itemName = slugifyAddonKey(item?.name);
    const itemNameEs = slugifyAddonKey(item?.name_es);

    return (
      (addonId && itemId === addonId) ||
      (addonName && (itemName === addonName || itemNameEs === addonName)) ||
      (addonNameEs && (itemName === addonNameEs || itemNameEs === addonNameEs))
    );
  });
};

export const hydrateAddonFromCatalog = (addon, catalog) => {
  const matched = findCatalogAddonMatch(catalog, addon) || {};
  const fallbackPrice = parseAddonPrice(addon?.price, parseAddonPrice(matched?.price, 0));
  const customPrice = addon?.custom_price;

  return {
    ...matched,
    ...addon,
    id: addon?.id || matched?.id || slugifyAddonKey(`${matched?.category || addon?.category || "addon"}_${addon?.name || matched?.name || "item"}`),
    name: addon?.name || matched?.name || "",
    name_es: addon?.name_es || matched?.name_es || addon?.name || matched?.name || "",
    category: addon?.category || matched?.category || "other",
    category_label: addon?.category_label || matched?.category_label,
    category_label_es: addon?.category_label_es || matched?.category_label_es,
    qty: addon?.qty || addon?.quantity || 1,
    price: fallbackPrice,
    custom_price: customPrice ?? fallbackPrice,
    original_price: parseAddonPrice(addon?.original_price, fallbackPrice),
  };
};

export function calculateOrderTotal(order, payMethod) {
  const storedTotal = Number(order.total_amount ?? order.extra_charge ?? 0);
  const deliveryFee = Number(order.delivery_fee ?? calcDeliveryFee(order.distance_miles) ?? 0);
  const addonsTotal = (order.addon_services || []).reduce(
    (s, a) => s + Number(a.custom_price || a.price || 0) * Number(a.qty || a.quantity || 1), 0
  );
  if (storedTotal > 0) {
    const baseTotal = Math.round(storedTotal * 100) / 100;
    return {
      baseTotal, total: baseTotal,
      discount: Number(order.membership_discount || 0),
      deliveryFee, addonsTotal,
      lbsCovered: Number(order.lbs_from_allowance || 0),
      lbsExtra: Number(order.extra_lbs_billed || 0),
      isMember: Boolean(order.membership_plan),
      plan: (order.service_plan || "standard").toLowerCase(),
      lbs: Number(order.actual_lbs || 0),
      allowanceExhausted: Boolean(order.membership_plan) && Number(order.lbs_from_allowance || 0) === 0 && Number(order.actual_lbs || 0) > 0,
    };
  }
  const breakdown = buildDisplayBreakdown(order);
  return {
    baseTotal: breakdown.total, total: breakdown.total,
    discount: breakdown.discount, deliveryFee: breakdown.deliveryFee,
    addonsTotal: breakdown.addonsTotal, lbsCovered: breakdown.lbsCovered,
    lbsExtra: breakdown.lbsExtra, isMember: breakdown.isMember,
    plan: breakdown.plan, lbs: breakdown.lbs,
    allowanceExhausted: breakdown.allowanceExhausted,
  };
}

export function getRegisteredPaymentAmount(order, payMethod) {
  const { total } = calculateOrderTotal(order, payMethod);
  const normalizedMethod = normalizePayMethod(payMethod);
  const surchargeMultiplier = normalizedMethod === "card" ? 1.03 : 1;
  return Math.round(total * surchargeMultiplier * 100) / 100;
}

// Default catalog with standard prices
export const DEFAULT_ADDON_CATALOG = [
  { id: "bath_mat",       name: "Bath Mat",         price: 8.00,   category: "home_essentials" },
  { id: "Oven Mitt",      name: "Cojín para horno", price: 8.00,   category: "home_essentials" },
  { id: "pet_bed_s",      name: "Pet Bed (S)",       price: 15.00,  category: "home_essentials" },
  { id: "pet_bed_ml",     name: "Pet Bed (M/L)",     price: 18.00,  category: "home_essentials" },
  { id: "pillow_std",     name: "Pillow Std",        price: 10.00,  category: "bedding" },
  { id: "pillow_lg",      name: "Pillow Lg",         price: 15.00,  category: "bedding" },
  { id: "duvet_cover",    name: "Duvet Cover",       price: 15.00,  category: "bedding" },
  { id: "blanket",        name: "Blanket",           price: 15.00,  category: "bedding" },
  { id: "comforter_tdq",  name: "Comforter T/D/Q",  price: 25.00,  category: "comforters" },
  { id: "comforter_king", name: "Comforter King",   price: 30.00,  category: "comforters" },
  { id: "mattress_cover", name: "Mattress Cover",   price: 25.00,  category: "comforters" },
  { id: "down_comforter", name: "Down Comforter",   price: 45.00, category: "comforters" },
];

// ─── RECURRENCE LABELS ────────────────────────────────────────────────────────
export const RECURRENCE_LABELS = {
  en: {
    once: "One time",
    weekly: "Weekly",
    biweekly: "Every 2 weeks",
    twice_week: "Twice a week",
  },
  es: {
    once: "Una vez",
    weekly: "Semanal",
    biweekly: "Cada 2 semanas",
    twice_week: "Dos veces por semana",
  },
};

export const RECURRENCE_WEEKDAYS = [
  { value: "mon", en: "Mon", es: "Lun" },
  { value: "tue", en: "Tue", es: "Mar" },
  { value: "wed", en: "Wed", es: "Mié" },
  { value: "thu", en: "Thu", es: "Jue" },
  { value: "fri", en: "Fri", es: "Vie" },
  { value: "sat", en: "Sat", es: "Sáb" },
  { value: "sun", en: "Sun", es: "Dom" },
];

export const groupBy = (arr, key) =>
  arr.reduce((acc, x) => {
    const k = x[key] || "other";
    (acc[k] = acc[k] || []).push(x);
    return acc;
  }, {});

export const STATUS_CONFIG = {
  new:              { color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  confirmed:        { color: "bg-blue-100 text-blue-800 border-blue-200" },
  picked_up:        { color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  processing:       { color: "bg-purple-100 text-purple-800 border-purple-200" },
  ready:            { color: "bg-teal-100 text-teal-800 border-teal-200" },
  out_for_delivery: { color: "bg-orange-100 text-orange-800 border-orange-200" },
  delivered:        { color: "bg-green-100 text-green-800 border-green-200" },
  completed:        { color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelled:        { color: "bg-red-100 text-red-800 border-red-200" },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

export const Section = forwardRef(function Section(
  { icon, title, badge, children, collapsible = false, defaultOpen = true, className = "", ...rest },
  ref
) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      ref={ref}
      className={`rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm ${className}`}
      {...rest}
    >
      <div
        className={`flex items-center gap-2 sm:gap-2.5 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 ${collapsible ? "cursor-pointer select-none" : ""}`}
        onClick={() => collapsible && setOpen(v => !v)}
      >
        <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-sky-50 text-sky-600 shrink-0">{icon}</span>
        <h3 className="font-semibold text-slate-800 text-sm flex-1">{title}</h3>
        {badge}
        {collapsible && (
          <span className="text-slate-400">
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        )}
      </div>
      {(!collapsible || open) && <div className="p-3 sm:p-4">{children}</div>}
    </div>
  );
});

export function DataRow({ label, value, className = "", mono = false }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className={`space-y-0.5 ${className}`}>
      <p className="text-[10px] font-semibold tracking-widest text-slate-400 uppercase">{label}</p>
      <p className={`text-sm font-medium text-slate-800 leading-snug ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

// ─── Preferred contact method (per-order override) ─────────────────────────
export const CONTACT_METHOD_LABELS = {
  sms:      { en: "SMS / Text",  es: "SMS / Texto" },
  email:    { en: "Email",       es: "Correo" },
  call:     { en: "Phone call",  es: "Llamada" },
  whatsapp: { en: "WhatsApp",    es: "WhatsApp" },
};

export function ContactMethodRow({ order, onSave, saving, t, locale }) {
  const [editing, setEditing] = useState(false);
  const current = order?.preferred_contact || "sms";
  const [value, setValue] = useState(current);

  useEffect(() => {
    if (!editing) setValue(current);
  }, [current, editing]);

  if (!current && !editing) return null;

  const label = CONTACT_METHOD_LABELS[current]?.[locale === "es" ? "es" : "en"] || current;

  if (!editing) {
    return (
      <div className="mt-2 flex items-end justify-between gap-2">
        <DataRow label={t("Preferred contact", "Contacto preferido")} value={label} />
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="mb-0.5 text-[10px] font-semibold text-sky-600 hover:text-sky-700 flex items-center gap-1"
          data-testid="contact-method-edit-btn"
        >
          <Edit2 className="w-3 h-3" />{t("Change", "Cambiar")}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-2">
      <Label className="text-[10px] font-bold text-slate-400 uppercase">
        {t("Preferred contact (this order only)", "Contacto preferido (solo esta orden)")}
      </Label>
      <select
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full h-9 text-sm border border-slate-200 rounded-lg px-2.5 bg-white"
        data-testid="contact-method-select"
      >
        {Object.entries(CONTACT_METHOD_LABELS).map(([val, l]) => (
          <option key={val} value={val}>{locale === "es" ? l.es : l.en}</option>
        ))}
      </select>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={saving}
          onClick={async () => {
            const ok = await onSave?.(value);
            if (ok) setEditing(false);
          }}
          data-testid="contact-method-save"
        >
          {saving ? t("Saving…", "Guardando…") : t("Save", "Guardar")}
        </Button>
        <Button size="sm" variant="outline" disabled={saving} onClick={() => setEditing(false)} data-testid="contact-method-cancel">
          {t("Cancel", "Cancelar")}
        </Button>
      </div>
    </div>
  );
}

export function InfoChip({ icon, label, value, color = "sky" }) {
  const colors = {
    sky:     "bg-sky-50 border-sky-200 text-sky-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-800",
    red:     "bg-red-50 border-red-200 text-red-800",
    purple:  "bg-purple-50 border-purple-200 text-purple-800",
  };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold ${colors[color]}`}>
      {icon}
      <span>{label && <span className="opacity-60 mr-1">{label}:</span>}{value}</span>
    </div>
  );
}

export function PayMethodBtn({ label, icon, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 py-2.5 px-1 rounded-xl border-2 text-[11px] font-semibold transition-all ${
        active
          ? "bg-sky-600 text-white border-sky-600 shadow-md shadow-sky-200"
          : "bg-white text-slate-500 border-slate-200 hover:border-sky-300 hover:text-sky-600"
      }`}
    >
      <span className={active ? "text-white" : "text-slate-400"}>{icon}</span>
      {label}
    </button>
  );
}

export function ReceiptCard({ receipt, onValidate, validating }) {
  const { t } = useLocale();
  const [expanded, setExpanded]     = useState(false);
  const [blobUrl, setBlobUrl]       = useState(null);
  const [imgError, setImgError]     = useState(false);
  const [imgLoading, setImgLoading] = useState(true);

  useEffect(() => {
    let objectUrl = null;
    setImgLoading(true);
    setImgError(false);
    const load = async (retry = true) => {
      try {
        const res = await fetch(`${API_URL}/api/files/${receipt.id}/download`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (handle401(res)) return;
        if (!res.ok) throw new Error();
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        if (retry) setTimeout(() => load(false), 1000);
        else { setImgError(true); setImgLoading(false); }
      }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [receipt.id]);

  const stCfg = {
    verified_paid: { icon: <ShieldCheck className="w-4 h-4" />, label: t("AI: Verified payment", "IA: Pago verificado"), cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
    rejected:      { icon: <ShieldX    className="w-4 h-4" />, label: t("AI: Not valid",        "IA: No válido"),        cls: "bg-red-50 border-red-200 text-red-700" },
    pending:       { icon: <Clock      className="w-4 h-4" />, label: t("Pending AI",            "Pendiente IA"),         cls: "bg-amber-50 border-amber-200 text-amber-700" },
  };
  const st = stCfg[receipt.ai_validation_status || "pending"] ?? stCfg.pending;

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b border-slate-100">
        <ImageIcon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-xs font-medium text-slate-600 truncate flex-1">{receipt.original_filename || "receipt"}</span>
        <span className="text-[10px] text-slate-400 shrink-0">
          {receipt.created_at ? new Date(receipt.created_at).toLocaleString() : ""}
        </span>
      </div>
      <div
        className={`relative bg-slate-100 cursor-pointer ${expanded ? "min-h-[200px]" : "min-h-[120px]"}`}
        onClick={() => !imgLoading && !imgError && setExpanded(v => !v)}
      >
        {imgLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
          </div>
        )}
        {imgError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-slate-400">
            <AlertTriangle className="w-6 h-6" />
            <span className="text-[11px]">{t("Could not load", "No se pudo cargar")}</span>
          </div>
        )}
        {blobUrl && !imgError && (
          <img
            src={blobUrl}
            alt="Receipt"
            className={`w-full object-contain ${expanded ? "max-h-[520px]" : "max-h-[160px]"}`}
            onLoad={() => setImgLoading(false)}
            onError={() => { setImgError(true); setImgLoading(false); }}
          />
        )}
        {!imgLoading && !imgError && blobUrl && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            className="absolute bottom-2 right-2 bg-black/50 text-white rounded-lg px-2 py-1 text-[10px] flex items-center gap-1 hover:bg-black/70"
          >
            <Eye className="w-3 h-3" />
            {expanded ? t("Collapse", "Colapsar") : t("Expand", "Ampliar")}
          </button>
        )}
      </div>
      <div className={`px-3 py-2.5 flex items-start gap-2.5 border-t border-slate-100 ${st.cls}`}>
        <span className="shrink-0 mt-0.5">{st.icon}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold">{st.label}</p>
          {receipt.ai_validation_notes    && <p className="text-[11px] mt-0.5 opacity-80">{receipt.ai_validation_notes}</p>}
          {receipt.ai_extracted_amount > 0 && <p className="text-[11px] mt-0.5 font-bold">{t("Amount", "Monto")}: ${Number(receipt.ai_extracted_amount).toFixed(2)}</p>}
        </div>
        {(receipt.ai_validation_status || "pending") === "pending" && (
          <Button
            size="sm"
            className="h-7 px-2.5 text-[10px] bg-sky-600 hover:bg-sky-700 shrink-0"
            onClick={() => onValidate(receipt.id)}
            disabled={validating === receipt.id}
          >
            {validating === receipt.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : t("Validate", "Validar")}
          </Button>
        )}
      </div>
    </div>
  );
}

export function EvidenceImageThumb({ orderId, type, label, onOpen }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!orderId) return undefined;
    let objectUrl = null;
    let cancelled = false;

    setLoading(true);
    setHasError(false);
    setBlobUrl(null);

    const load = async () => {
      try {
        const res = await fetch(buildEvidenceImageUrl(orderId, type), {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (handle401(res) || handle403(res)) return;
        if (!res.ok) throw new Error(`image ${type} not available`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setBlobUrl(objectUrl);
      } catch {
        if (!cancelled) setHasError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orderId, type]);

  return (
    <div
      className={`relative rounded-xl overflow-hidden border shadow-sm transition-all ${
        blobUrl ? "cursor-pointer border-slate-200 bg-white hover:shadow-md" : "border-slate-100 bg-slate-50"
      }`}
      onClick={() => blobUrl && onOpen?.(blobUrl)}
    >
      <div className="h-28">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <RefreshCw className="w-5 h-5 animate-spin" />
          </div>
        ) : blobUrl ? (
          <>
            <img src={blobUrl} alt={label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/35 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
              <ZoomIn className="w-5 h-5 text-white" />
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1 text-slate-400">
            <Camera className="w-6 h-6 opacity-40" />
            <span className="text-[11px]">{hasError ? "No image yet" : "Unavailable"}</span>
          </div>
        )}
      </div>
      <div className="px-2.5 py-2 border-t border-slate-100 bg-white/90">
        <p className="text-[11px] font-semibold text-slate-700 truncate">{label}</p>
      </div>
    </div>
  );
}

// ─── ADD-ON PRICE EDITOR COMPONENT ────────────────────────────────────────────
export function AddonPriceEditor({ addon, onSave, onCancel }) {
  const [customPrice, setCustomPrice] = useState(String(addon.custom_price || addon.price || 0));

  return (
    <div className="absolute top-full right-0 mt-1 z-20 bg-white border border-slate-200 rounded-xl shadow-lg p-2 min-w-[140px] max-w-[calc(100vw-2rem)]">
      <div className="flex items-center gap-1">
        <span className="text-xs text-slate-500">$</span>
        <Input
          type="number"
          step="0.01"
          min="0"
          value={customPrice}
          onChange={(e) => setCustomPrice(e.target.value)}
          className="h-7 text-xs w-24 rounded"
          autoFocus
        />
        <button
          onClick={() => onSave(Number(customPrice))}
          className="w-7 h-7 bg-sky-600 text-white rounded-lg hover:bg-sky-700 flex items-center justify-center"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onCancel}
          className="w-7 h-7 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 flex items-center justify-center"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── RECURRENCE COMPONENT (editable from the operator side) ──────────────────
export function RecurrenceInfo({ order, t, locale, onSave, saving }) {
  const [upcomingOrders, setUpcomingOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editRecurrence, setEditRecurrence] = useState(order?.recurrence || "once");
  const [editDays, setEditDays] = useState(order?.recurrence_days || []);
  const [editEndDate, setEditEndDate] = useState(
    order?.recurrence_end_date ? String(order.recurrence_end_date).slice(0, 10) : ""
  );

  const isRecurring = order?.is_recurring === true && order?.recurrence !== "once";
  const recurrenceKey = order?.recurrence || "once";
  const langKey = locale === "es" ? "es" : "en";
  const recurrenceLabel = RECURRENCE_LABELS[langKey][recurrenceKey] || RECURRENCE_LABELS.en[recurrenceKey];
  const recurrenceDays = order?.recurrence_days || [];

  useEffect(() => {
    if (!editing) {
      setEditRecurrence(order?.recurrence || "once");
      setEditDays(order?.recurrence_days || []);
      setEditEndDate(order?.recurrence_end_date ? String(order.recurrence_end_date).slice(0, 10) : "");
    }
  }, [order, editing]);

  const fetchUpcomingOrders = useCallback(async () => {
    if (!order?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/orders/${order.id}/recurrence`, {
        headers: authHdrs(),
      });
      if (res.ok) {
        const data = await res.json();
        setUpcomingOrders(data.upcoming_pickups || []);
      }
    } catch (err) {
      console.error("Failed to fetch upcoming recurring orders:", err);
    } finally {
      setLoading(false);
    }
  }, [order?.id]);

  useEffect(() => {
    if (showUpcoming && isRecurring) {
      fetchUpcomingOrders();
    }
  }, [showUpcoming, isRecurring, fetchUpcomingOrders]);

  const toggleEditDay = (day) => {
    setEditDays((prev) => {
      if (prev.includes(day)) return prev.filter((d) => d !== day);
      if (editRecurrence === "twice_week" && prev.length >= 2) return [prev[1], day];
      return [...prev, day];
    });
  };

  const handleStartEdit = (e) => {
    e?.stopPropagation?.();
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditRecurrence(order?.recurrence || "once");
    setEditDays(order?.recurrence_days || []);
    setEditEndDate(order?.recurrence_end_date ? String(order.recurrence_end_date).slice(0, 10) : "");
  };

  const handleSaveEdit = async () => {
    const payload = {
      recurrence: editRecurrence,
      is_recurring: editRecurrence !== "once",
      recurrence_days: editRecurrence === "twice_week" ? editDays : [],
      recurrence_end_date: editRecurrence !== "once" && editEndDate ? editEndDate : null,
    };
    const ok = await onSave?.(payload);
    if (ok) setEditing(false);
  };

  const editForm = (
    <div className="space-y-3">
      <div>
        <Label className="text-[10px] font-bold text-slate-400 uppercase">{t("Frequency", "Frecuencia")}</Label>
        <select
          value={editRecurrence}
          onChange={(e) => setEditRecurrence(e.target.value)}
          className="w-full mt-1 h-9 text-sm border border-slate-200 rounded-lg px-2.5 bg-white"
          data-testid="recurrence-select"
        >
          <option value="once">{RECURRENCE_LABELS[langKey].once}</option>
          <option value="weekly">{RECURRENCE_LABELS[langKey].weekly}</option>
          <option value="biweekly">{RECURRENCE_LABELS[langKey].biweekly}</option>
          <option value="twice_week">{RECURRENCE_LABELS[langKey].twice_week}</option>
        </select>
      </div>

      {editRecurrence === "twice_week" && (
        <div>
          <Label className="text-[10px] font-bold text-slate-400 uppercase">{t("Days (pick 2)", "Días (elige 2)")}</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {RECURRENCE_WEEKDAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleEditDay(d.value)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  editDays.includes(d.value)
                    ? "bg-purple-600 text-white border-purple-600"
                    : "bg-white text-slate-500 border-slate-200 hover:border-purple-300"
                }`}
                data-testid={`recurrence-day-${d.value}`}
              >
                {d[langKey]}
              </button>
            ))}
          </div>
          {editDays.length !== 2 && (
            <p className="text-[11px] text-amber-600 mt-1">{t("Pick exactly 2 days", "Elige exactamente 2 días")}</p>
          )}
        </div>
      )}

      {editRecurrence !== "once" && (
        <div>
          <Label className="text-[10px] font-bold text-slate-400 uppercase">{t("End date (optional)", "Fecha fin (opcional)")}</Label>
          <Input
            type="date"
            value={editEndDate}
            onChange={(e) => setEditEndDate(e.target.value)}
            className="mt-1 h-9 text-sm"
            data-testid="recurrence-end-date"
          />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          onClick={handleSaveEdit}
          disabled={saving || (editRecurrence === "twice_week" && editDays.length !== 2)}
          className="flex-1 h-9 bg-purple-600 hover:bg-purple-700 text-xs rounded-xl"
          data-testid="recurrence-save"
        >
          {saving && <RefreshCw className="w-3 h-3 animate-spin mr-1" />}
          {t("Save frequency", "Guardar frecuencia")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={handleCancelEdit}
          disabled={saving}
          className="flex-1 h-9 text-xs rounded-xl"
          data-testid="recurrence-cancel"
        >
          {t("Cancel", "Cancelar")}
        </Button>
      </div>
    </div>
  );

  if (!isRecurring && !editing) {
    return (
      <Section
        icon={<Repeat className="w-4 h-4" />}
        title={t("Recurring Order", "Orden Recurrente")}
        collapsible={false}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs text-slate-400">{t("This order is one-time.", "Esta orden es de una sola vez.")}</span>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs gap-1.5 rounded-lg"
            onClick={handleStartEdit}
            data-testid="recurrence-set-frequency"
          >
            <Edit2 className="w-3.5 h-3.5" />{t("Set frequency", "Definir frecuencia")}
          </Button>
        </div>
      </Section>
    );
  }

  return (
    <Section
      icon={<Repeat className="w-4 h-4" />}
      title={t("Recurring Order", "Orden Recurrente")}
      collapsible={true}
      defaultOpen={true}
      badge={
        !editing ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-bold text-purple-600 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5">
              {recurrenceLabel}
            </span>
            <button
              onClick={handleStartEdit}
              className="w-6 h-6 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 flex items-center justify-center"
              title={t("Edit frequency", "Editar frecuencia")}
              data-testid="recurrence-edit-btn"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          </div>
        ) : null
      }
    >
      {editing ? (
        editForm
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <InfoChip
              icon={<CalendarRange className="w-3.5 h-3.5" />}
              label={t("Frequency", "Frecuencia")}
              value={recurrenceLabel}
              color="purple"
            />
            {recurrenceDays.length > 0 && (
              <InfoChip
                icon={<CalendarDays className="w-3.5 h-3.5" />}
                label={t("Days", "Días")}
                value={recurrenceDays.join(", ")}
                color="sky"
              />
            )}
            {order?.recurrence_end_date && (
              <InfoChip
                icon={<Calendar className="w-3.5 h-3.5" />}
                label={t("End date", "Fecha fin")}
                value={new Date(order.recurrence_end_date).toLocaleDateString()}
                color="amber"
              />
            )}
          </div>

          <button
            onClick={() => setShowUpcoming(!showUpcoming)}
            className="flex items-center gap-1.5 text-xs font-medium text-sky-600 hover:text-sky-700 transition-colors"
          >
            {showUpcoming ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {showUpcoming ? t("Hide upcoming", "Ocultar próximos") : t("Show upcoming pickups", "Ver próximos pickups")}
          </button>

          {showUpcoming && (
            <div className="mt-2 rounded-xl border border-slate-100 bg-slate-50/50 overflow-hidden">
              {loading ? (
                <div className="p-4 text-center text-slate-400 text-xs">
                  <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-1" />
                  {t("Loading...", "Cargando...")}
                </div>
              ) : upcomingOrders.length === 0 ? (
                <div className="p-4 text-center text-slate-400 text-xs">
                  <Calendar className="w-5 h-5 opacity-30 mx-auto mb-1" />
                  {t("No upcoming recurring pickups", "No hay pickups recurrentes próximos")}
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {upcomingOrders.map((uo) => (
                    <div key={uo.id} className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs font-mono text-slate-600">#{uo.order_number}</span>
                      </div>
                      <span className="text-xs font-medium text-slate-700">
                        {new Date(uo.pickup_date).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Section>
  );
}
